import { Search } from "lucide-react";
import { type ChangeEvent, type ReactNode, useMemo, useState } from "react";

import type { CustomerSummary } from "@busright/data-contract";

import { CustomerHealthChip } from "@/components/CustomerHealthChip";
import { DataTable } from "@/components/DataTable";
import { formatInt, formatUsd } from "@/lib/format";

export interface CustomerTableProps {
  readonly customers: readonly CustomerSummary[];
  // Drill-through to /customers/:id.
  readonly onRowClick: (customer: CustomerSummary) => void;
}

type SortKey = "arr" | "tickets" | "health";

const SORT_OPTIONS: readonly { readonly value: SortKey; readonly label: string }[] = [
  { value: "arr", label: "ARR" },
  { value: "tickets", label: "Open tickets" },
  { value: "health", label: "At-risk devices" },
];

// Weight a CRITICAL device far above a WARN so any critical load sorts ahead of a pile of warns.
const CRITICAL_WEIGHT = 1000;

// At-risk load for the "health" sort: CRIT dominates, WARN breaks ties. Unmatched (no health) sinks.
const healthRisk = (c: CustomerSummary): number => {
  if (!c.health) {
    return -1;
  }
  return c.health.riskCounts.critical * CRITICAL_WEIGHT + c.health.riskCounts.warn;
};

const sortValue = (c: CustomerSummary, key: SortKey): number => {
  if (key === "arr") {
    return c.revenueUsd ?? -1;
  }
  if (key === "tickets") {
    return c.tickets.total;
  }
  return healthRisk(c);
};

// The Linear-led customer breakdown: searchable by name, sortable by ARR / open tickets / device
// at-risk load. Rows drill into /customers/:id. Sort is always descending (biggest / worst first) —
// the only ordering that matters for a worklist.
export const CustomerTable = ({ customers, onRowClick }: CustomerTableProps): ReactNode => {
  const [query, setQuery] = useState("");
  const [sortKey, setSortKey] = useState<SortKey>("arr");

  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const filtered =
      needle.length > 0
        ? customers.filter((c) => c.name.toLowerCase().includes(needle))
        : customers;
    return filtered.slice().sort((a, b) => sortValue(b, sortKey) - sortValue(a, sortKey));
  }, [customers, query, sortKey]);

  return (
    <div>
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: 12,
          alignItems: "center",
          marginBottom: 14,
        }}
      >
        <div style={{ position: "relative", flex: "1 1 240px", maxWidth: 360 }}>
          <Search
            size={15}
            aria-hidden
            style={{
              position: "absolute",
              top: "50%",
              left: 10,
              transform: "translateY(-50%)",
              color: "var(--br-text-muted)",
              pointerEvents: "none",
            }}
          />
          <input
            type="search"
            value={query}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setQuery(e.target.value)}
            placeholder="Search customers by name…"
            aria-label="Search customers by name"
            style={{
              width: "100%",
              padding: "7px 12px 7px 32px",
              fontSize: 13,
              color: "var(--br-text-primary)",
              background: "var(--br-surface-2)",
              border: "1px solid var(--br-border)",
              borderRadius: "var(--br-radius-sm)",
            }}
          />
        </div>

        <label style={{ display: "inline-flex", gap: 6, alignItems: "center" }}>
          <span
            style={{
              fontSize: 11,
              fontWeight: 600,
              letterSpacing: "0.04em",
              textTransform: "uppercase",
              color: "var(--br-text-muted)",
            }}
          >
            Sort by
          </span>
          <select
            value={sortKey}
            onChange={(e: ChangeEvent<HTMLSelectElement>) => setSortKey(e.target.value as SortKey)}
            style={{
              padding: "6px 10px",
              fontSize: 13,
              fontWeight: 600,
              color: "var(--br-text-primary)",
              cursor: "pointer",
              background: "var(--br-surface-2)",
              border: "1px solid var(--br-border)",
              borderRadius: "var(--br-radius-sm)",
            }}
          >
            {SORT_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </label>

        <span className="br-tabular-nums" style={{ fontSize: 12, color: "var(--br-text-muted)" }}>
          {formatInt(visible.length)} of {formatInt(customers.length)}
        </span>
      </div>

      <DataTable
        rows={visible}
        rowKey={(c) => c.id}
        onRowClick={onRowClick}
        emptyTitle="No customers match"
        emptyMessage="Try a different search."
        columns={[
          {
            key: "name",
            header: "Customer",
            render: (c) => (
              <div style={{ display: "flex", flexDirection: "column" }}>
                <span>{c.name}</span>
                <span className="mono" style={{ fontSize: 11, color: "var(--br-text-muted)" }}>
                  {c.state ?? "—"}
                </span>
              </div>
            ),
          },
          {
            key: "status",
            header: "Status",
            render: (c) => <span>{c.status ?? "—"}</span>,
          },
          {
            key: "owner",
            header: "Owner",
            render: (c) =>
              c.owner ? (
                <span>{c.owner}</span>
              ) : (
                <span style={{ color: "var(--br-text-muted)" }}>—</span>
              ),
          },
          {
            key: "tier",
            header: "Tier",
            render: (c) =>
              c.tier ? (
                <span>{c.tier}</span>
              ) : (
                <span style={{ color: "var(--br-text-muted)" }}>—</span>
              ),
          },
          {
            key: "arr",
            header: "ARR",
            align: "right",
            render: (c) => (
              <span className="mono br-tabular-nums">
                {c.revenueUsd === null ? "—" : formatUsd(c.revenueUsd)}
              </span>
            ),
          },
          {
            key: "tickets",
            header: "Open tickets",
            align: "right",
            render: (c) => (
              <span className="mono br-tabular-nums">{formatInt(c.tickets.total)}</span>
            ),
          },
          {
            key: "district",
            header: "Matched district",
            render: (c) =>
              c.districtId ? (
                <span className="mono" style={{ color: "var(--br-text-primary)" }}>
                  matched
                </span>
              ) : (
                <span className="mono" style={{ color: "var(--br-text-muted)" }}>
                  unmatched
                </span>
              ),
          },
          {
            key: "health",
            header: "Device health (OK / WARN / CRIT)",
            align: "right",
            render: (c) => <CustomerHealthChip riskCounts={c.health?.riskCounts ?? null} />,
          },
        ]}
      />
    </div>
  );
};
